import { PlayerWrapper } from "@infinitaslearning/app-player";
import _ from "lodash";
import type React from "react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { LoadingSpinner } from "components/LoadingSpinner";
import { useLanguage } from "hooks/useSelectedLanguage";

export interface PlayerProps {
  id: string;
  // biome-ignore lint/suspicious/noExplicitAny: PlayerWrapper accepts dynamic content structure
  data?: any;
  // biome-ignore lint/suspicious/noExplicitAny: PlayerWrapper accepts dynamic config structure
  config?: any;
  context?: "drilling" | "slimStampen";
}

export const Player: React.FC<PlayerProps> = ({ id, data, config = {}, context }) => {
  const { t } = useTranslation("common");
  const { currentLanguage } = useLanguage();

  // biome-ignore lint/correctness/useExhaustiveDependencies: Translations must change if user switches language
  const defaultConfig = useMemo(
    () => ({
      translations: {
        abstract: t("common:player.abstract"),
        advert: t("common:player.advert"),
        advert_flyer: t("common:player.advertFlyer"),
        almost_correct_body: t("common:player.almostCorrectBody"),
        almost_correct_title: t("common:player.almostCorrectTitle"),
        asset_download: t("common:player.assetDownload"),
        asset_downloaded: t("common:player.assetDownloaded"),
        asset_preview: t("common:player.assetPreview"),
        attention: t("common:player.attention"),
        back_to_exercise: t("common:player.backToExercise"),
        brief_summary: t("common:player.briefSummary"),
        bulk_vocabulary_listen_all: t("common:player.bulkVocabularyListenAll"),
        clock_correct_feedback: t("common:player.clock.correctFeedback"),
        clock_hours: t("common:player.clock.hours"),
        clock_incorrect_feedback: t("common:player.clock.incorrectFeedback"),
        clock_keyboard_five: t("common:player.clock.keyboardFive"),
        clock_keyboard_half: t("common:player.clock.keyboardHalf"),
        clock_keyboard_oclock: t("common:player.clock.keyboardOclock"),
        clock_keyboard_past: t("common:player.clock.keyboardPast"),
        clock_keyboard_quarter: t("common:player.clock.keyboardQuarter"),
        clock_keyboard_ten: t("common:player.clock.keyboardTen"),
        clock_keyboard_to: t("common:player.clock.keyboardTo"),
        clock_keyboard_twenty: t("common:player.clock.keyboardTwenty"),
        clock_minutes: t("common:player.clock.minutes"),
        close_full_screen: t("common:player.closeFullScreen"),
        complexAsset_missing: t("common:player.complexAssetMissing"),
        confirmation_text: t("common:player.confirmationText"),
        core_sentence: t("common:player.coreSentence"),
        correctAnswer_general: t("common:player.correctAnswer.general"),
        correctAnswer_title: t("common:player.correctAnswer.title"),
        correctAnswerSwitch_correct: t("common:player.correctAnswerSwitch.correct"),
        correctAnswerSwitch_current: t("common:player.correctAnswerSwitch.current"),
        definition_list: t("common:player.definitionList"),
        dragInteractions_originBox: t("common:player.dragInteractionOriginBox"),
        drilling_bad_score_subtitle: t("common:player.drilling.badScore.subtitle"),
        drilling_bad_score_title: t("common:player.drilling.badScore.title"),
        drilling_case_sensitivity: t("common:player.drilling.caseSensitivity"),
        drilling_check_exercise: t("common:player.drilling.checkExercise"),
        drilling_contains_flashcards: t("common:player.drilling.containsFlashcards"),
        drilling_continue_exercise: t("common:player.drilling.continueExercise"),
        drilling_finish_button: t("common:player.drilling.finishButton"),
        drilling_flashcard_finish_subtitle: t("common:player.drilling.flashcard.finishSubtitle"),
        drilling_flashcard_finish_title: t("common:player.drilling.flashcard.finishTitle"),
        drilling_flashcard_flashcard_redo_button: t("common:player.drilling.flashcard.redoButton"),
        drilling_flashcard_show_definitions: t("common:player.drilling.flashcard.showDefinitions"),
        drilling_flashcard_show_terms: t("common:player.drilling.flashcard.showTerms"),
        drilling_flashcard_subtitle: t("common:player.drilling.flashcard.subtitle"),
        drilling_generic_score_subtitle: t("common:player.drilling.genericScore.subtitle"),
        drilling_generic_score_title: t("common:player.drilling.genericScore.title"),
        drilling_go_to_next_flashcard: t("common:player.drilling.goToNextFlashcard"),
        drilling_good_score_subtitle: t("common:player.drilling.goodScore.subtitle"),
        drilling_good_score_title: t("common:player.drilling.goodScore.title"),
        drilling_intermediate_subtitle: t("common:player.drilling.intermediateSubtitle"),
        drilling_new_word: t("common:player.drilling.newWord"),
        drilling_perfect_score_title: t("common:player.drilling.perfectScore.title"),
        drilling_progress_bar_attempts: t("common:player.drilling.progressBar.attempts"),
        drilling_progress_bar_flashcards: t("common:player.drilling.progressBar.flashcards"),
        drilling_progress_bar_question: t("common:player.drilling.progressBar.question"),
        drilling_progress_bar_score: t("common:player.drilling.progressBar.score"),
        drilling_receptive_learning_disabled: t("common:player.drilling.receptiveLearningDisabled"),
        drilling_receptive_learning_practice: t("common:player.drilling.receptiveLearningPractice"),
        drilling_retake_exercise: t("common:player.drilling.retakeExercise"),
        drilling_start_exercise: t("common:player.drilling.startExercise"),
        drilling_start_items_in_set: t("common:player.drilling.startItemsInSet"),
        drilling_start_pro_tip: t("common:player.drilling.startProTip"),
        drilling_start_screen_flashcards: t("common:player.drilling.startScreenFlashcards"),
        drilling_stop_dialog_continue_button: t("common:player.drilling.stopDialog.continueButton"),
        drilling_stop_dialog_stop_button: t("common:player.drilling.stopDialog.stopButton"),
        drilling_stop_dialog_subtitle: t("common:player.drilling.stopDialog.subtitle"),
        drilling_stop_dialog_title: t("common:player.drilling.stopDialog.title"),
        dropArea_dragHere: t("common:player.dropArea.dragHere"),
        dropdown_placeholder: t("common:player.dropdownPlaceholder"),
        empty_interaction_placeholder_subtitle: t(
          "common:player.emptyInteractionPlaceholder.subtitle",
        ),
        empty_interaction_placeholder_title: t("common:player.emptyInteractionPlaceholder.title"),
        error_http_notfound: t("common:player.error.http.notFound"),
        error_http_unatuhorized: t("common:player.error.http.unauthorized"),
        error_http_unexpected_error: t("common:player.error.http.unexpectedError"),
        error_no_content: t("common:player.error.noContent"),
        error_no_language_set: t("common:player.error.noLanguageSet"),
        error_something_went_wrong: t("common:player.error.somethingWentWrong"),
        error_unsupported_content_language: t("common:player.error.unsupportedContentLanguage"),
        example: t("common:player.example"),
        exercise_finished: t("common:player.exerciseFinished"),
        fact: t("common:player.fact"),
        fact_indepth: t("common:player.factInDepth"),
        fact_profile: t("common:player.factProfile"),
        factual_question: t("common:player.factualQuestion"),
        feedback_bad: t("common:player.feedback.bad"),
        feedback_good: t("common:player.feedback.good"),
        feedback_incomplete_body: t("common:player.feedback.incompleteBody"),
        feedback_incomplete: t("common:player.feedback.incomplete"),
        feedback_no_evaluation: t("common:player.feedback.noEvaluation"),
        finish: t("common:player.finish"),
        finished_already: t("common:player.finishedAlready"),
        form_comment_header: t("common:player.form.comment.header"),
        form_comment_label: t("common:player.form.comment.label"),
        form_comment_placeholder: t("common:player.form.comment.placeholder"),
        form_hide_all: t("common:player.form.hideAll"),
        form_protocol_close: t("common:player.form.protocolDialog.close"),
        form_protocol_title: t("common:player.form.protocolDialog.title"),
        form_read_protocol: t("common:player.form.readProtocol"),
        form_required_value: t("common:player.form.requiredValue"),
        form_snackbar_action_label: t("common:player.form.snackbarActionLabel"),
        form_snackbar_title: t("common:player.form.snackbarTitle"),
        form_sub_steps: t("common:player.form.subSteps"),
        form_survey_overview: t("common:player.form.surveyOverview"),
        fun_fact: t("common:player.funFact"),
        gallery_view: t("common:player.galleryView"),
        glossEntryFetchNotWorking_msg: t("common:player.glossEntryFetchNotWorking.message"),
        glossEntryFetchNotWorking_platform: t("common:player.glossEntryFetchNotWorking.platform"),
        grammar: t("common:player.grammar"),
        highlight: t("common:player.highlight"),
        hint: t("common:player.hint"),
        image_source: t("common:player.imageSource"),
        interaction_done: t("common:player.interactionDone"),
        intro: t("common:player.intro"),
        letter: t("common:player.letter"),
        linguineo_almost_feedback: t("common:player.linguineo.almostFeedback"),
        linguineo_correct_feedback: t("common:player.linguineo.correctFeedback"),
        linguineo_finish_dialog_subtitle: t("common:player.linguineo.finishDialog.subtitle"),
        linguineo_finish_dialog_title: t("common:player.linguineo.finishDialog.title"),
        linguineo_hint_title: t("common:player.linguineo.hintTitle"),
        linguineo_incorrect_feedback: t("common:player.linguineo.incorrectFeedback"),
        linguineo_instruction_title: t("common:player.linguineo.instructionTitle"),
        linguineo_level_A1_subtitle: t("common:player.linguineo.levelA1.subtitle"),
        linguineo_level_A1_title: t("common:player.linguineo.levelA1.title"),
        linguineo_level_B1_subtitle: t("common:player.linguineo.levelB1.subtitle"),
        linguineo_level_B1_title: t("common:player.linguineo.levelB1.title"),
        linguineo_level_C1_subtitle: t("common:player.linguineo.levelC1.subtitle"),
        linguineo_level_C1_title: t("common:player.linguineo.levelC1.title"),
        linguineo_next_button_title: t("common:player.linguineo.nextButton"),
        linguineo_pick_level_instruction: t("common:player.linguineo.pickLevelInstruction"),
        linguineo_record: t("common:player.linguineo.record"),
        linguineo_sound_off: t("common:player.linguineo.soundOff"),
        linguineo_stop_recording: t("common:player.linguineo.stopRecording"),
        linguineo_transcribing: t("common:player.linguineo.transcribing"),
        linguineo_try_again: t("common:player.linguineo.tryAgain"),
        linguineo_type_your_answer: t("common:player.linguineo.typeYourAnswer"),
        literature: t("common:player.literature"),
        lyrics_song: t("common:player.lyricsSong"),
        marginWords_title: t("common:player.marginWordsTitle"),
        memo: t("common:player.memo"),
        navigation_scroll_back: t("common:player.navigationScrollBack"),
        navigation_scroll_end: t("common:player.navigationScrollEnd"),
        next: t("common:player.next"),
        note: t("common:player.note"),
        open_full_screen: t("common:player.openFullScreen"),
        open_question_evaluation_checking: t("common:player.openQuestionEvaluationChecking"),
        pause_audio: t("common:player.pauseAudio"),
        play_audio: t("common:player.playAudio"),
        pronunciation: t("common:player.pronunciation"),
        read_less: t("common:player.readLess"),
        read_more: t("common:player.readMore"),
        receptive_learning_correct_answer: t("common:player.receptiveLearningCorrectAnswer"),
        required_answer: t("common:player.requiredAnswer"),
        required_self_assessment: t("common:player.requiredSelfAssessment"),
        reset: t("common:player.reset"),
        retry: t("common:player.retry"),
        review: t("common:player.review"),
        self_assessment_evaluation_correct: t("common:player.selfAssessment.evaluation.correct"),
        self_assessment_evaluation_incorrect: t(
          "common:player.selfAssessment.evaluation.incorrect",
        ),
        self_assessment_evaluation_partially_correct: t(
          "common:player.selfAssessment.evaluation.partiallyCorrect",
        ),
        selfAssess_bad: t("common:player.selfAssessment.manualSelfAssessment.bad"),
        selfAssess_good: t("common:player.selfAssessment.manualSelfAssessment.good"),
        selfAssess_normal: t("common:player.selfAssessment.manualSelfAssessment.normal"),
        selfAssess_question: t("common:player.selfAssessment.manualSelfAssessment.question"),
        show_self_assessment_solution_explained_click_here: t(
          "common:player.selfAssessment.showSolutionExplained.clickHere",
        ),
        show_self_assessment_solution_explained_content: t(
          "common:player.selfAssessment.showSolutionExplained.content",
        ),
        slim_stampen_all_mastered_subtitle: t("common:player.slimStampen.allMastered.subtitle"),
        slim_stampen_all_mastered_title: t("common:player.slimStampen.allMastered.title"),
        slim_stampen_choose_time: t("common:player.slimStampen.chooseTime"),
        slim_stampen_finish_subtitle: t("common:player.slimStampen.finish.subtitle"),
        slim_stampen_finish_title: t("common:player.slimStampen.finish.title"),
        slim_stampen_in_progress_term: t("common:player.slimStampen.inProgressTerm"),
        slim_stampen_instruction: t("common:player.slimStampen.instruction"),
        slim_stampen_intermediate_subtitle: t("common:player.slimStampen.intermediate.subtitle"),
        slim_stampen_intermediate_title: t("common:player.slimStampen.intermediate.title"),
        slim_stampen_mastered_term: t("common:player.slimStampen.masteredTerm"),
        slim_stampen_new_word: t("common:player.slimStampen.newWord"),
        slim_stampen_practice_round: t("common:player.slimStampen.practiceRound"),
        slim_stampen_pro_tip: t("common:player.slimStampen.proTip"),
        slim_stampen_time_to_practice: t("common:player.slimStampen.timeToPractice"),
        slim_stampen_timer_start: t("common:player.slimStampen.timerStart"),
        slim_stampen_tip: t("common:player.slimStampen.tip"),
        slim_stampen_unpracticed_term: t("common:player.slimStampen.unpracticedTerm"),
        smartAsset_missing: t("common:player.smartAssetMissing"),
        solutionExplained_title: t("common:player.solutionExplainedTitle"),
        submit_signOff: t("common:player.submitSignOff"),
        submit: t("common:player.submit"),
        summary: t("common:player.summary"),
        superSubmit: t("common:player.superSubmit"),
        table_show_remaining_rows: t("common:player.tableShowRemainingRows"),
        textEntry_placeholder: t("common:player.textEntryPlaceholder"),
        tip: t("common:player.tip"),
        turn: t("common:player.turn"),
        video_source: t("common:player.videoSource"),
        view_full_screen: t("common:player.viewFullScreen"),
        vocabulary: t("common:player.vocabulary"),
        web: t("common:player.web"),
        message_to: t("common:player.message.to"),
        message_from: t("common:player.message.from"),
        message_subject: t("common:player.message.subject"),
        message_date: t("common:player.message.date"),
        form_answer_option_false: t("common:player.formAnswerOption.false"),
        form_answer_option_true: t("common:player.formAnswerOption.true"),
        form_answer_option_disagree: t("common:player.formAnswerOption.disagree"),
        form_answer_option_somewhat_disagree: t("common:player.formAnswerOption.somewhatDisagree"),
        form_answer_option_neutral: t("common:player.formAnswerOption.neutral"),
        form_answer_option_somewhat_agree: t("common:player.formAnswerOption.somewhatAgree"),
        form_answer_option_agree: t("common:player.formAnswerOption.agree"),
        form_answer_option_sad: t("common:player.formAnswerOption.sad"),
        form_answer_option_somewhat_sad: t("common:player.formAnswerOption.somewhatSad"),
        form_answer_option_happy: t("common:player.formAnswerOption.happy"),
        form_answer_option_somewhat_happy: t("common:player.formAnswerOption.somewhatHappy"),
        form_answer_option_n_a: t("common:player.formAnswerOption.noAnswer"),
        poll_vote_plural: t("common:player.poll.votePlural"),
        poll_vote_singular: t("common:player.poll.voteSingular"),
        poll_show_results: t("common:player.poll.showResults"),
        poll_hide_results: t("common:player.poll.hideResults"),
      },
    }),
    [currentLanguage],
  );

  /* biome-ignore lint/correctness/useExhaustiveDependencies:
      We check "config" dependency by value instead of reference to avoid entering infinite loop */
  const playerConfig = useMemo(
    () => _.merge({}, defaultConfig, config),
    [defaultConfig, JSON.stringify(config)],
  );

  // Player doesn't automatically re-render when data or config changes, so we need to
  // force a refresh by not rendering the "PlayerWrapper" instance for a moment
  const [forceRefresh, setForceRefresh] = useState(false);
  /* biome-ignore lint/correctness/useExhaustiveDependencies:
      Hook needs to run whenever config or data changes.
      We check "data" dependency by value instead of reference to avoid entering infinite loop. */
  useEffect(() => {
    setTimeout(() => {
      setForceRefresh(false);
    }, 50);

    // This callback function will be called anytime the hook is about to run, so we
    // use it to set the force flag to "true" to indicate the dependencies have changed
    return () => {
      setForceRefresh(true);
    };
  }, [id, context, JSON.stringify(data), playerConfig]);

  // If there's no data, no point in showing anything
  if (!data) {
    return null;
  }

  // Temporarily display loader while we force a reload of the Player instance
  if (forceRefresh) {
    return <LoadingSpinner />;
  }

  return <PlayerWrapper id={id} config={playerConfig} context={context} initialData={data} />;
};
